<template>
  <div class="flow-common-history">

    <!-- 时间线模式 -->
    <template v-if="timeline">
      <div class="history-flat-timeline">
        <template v-if="flatTimelineItems.length">
          <div v-for="(entry, index) in flatTimelineItems" :key="index"
               class="flat-item" :class="entry.type === 'node' ? 'flat-item-node' : (entry.type === 'task' ? 'flat-item-task' : 'flat-item-record')">
            <div class="flat-left">
              <!-- 节点：图标 + 状态徽标 -->
              <template v-if="entry.type === 'node'">
                <div class="flat-avatar-wrapper">
                  <div class="flat-node-avatar" :class="getFlatNodeAvatarClass(entry)">
                    <i :class="getFlatNodeIcon(entry)"></i>
                  </div>
                  <div v-if="entry.nodeStatus" class="flat-node-status" :class="entry.nodeStatus === 'completed' ? 'status-completed' : 'status-running'">
                    <i :class="entry.nodeStatus === 'completed' ? 'el-icon-check' : 'el-icon-more'"></i>
                  </div>
                </div>
              </template>
              <!-- 任务/记录：小头像 -->
              <template v-else>
                <div class="flat-small-avatar" :class="getFlatDotClass(entry)">
                  <i class="el-icon-user"></i>
                </div>
              </template>
              <div v-if="index < flatTimelineItems.length - 1" class="flat-line"></div>
            </div>
            <div class="flat-right">

              <!-- 节点条目 -->
              <template v-if="entry.type === 'node'">
                <div class="flat-node-header">
                  <div class="flat-node-left">
                    <span v-if="entry.nodeName" class="flat-node-name">{{ entry.nodeName }}</span>
                    <div v-if="entry.isEndEvent" class="flat-node-sub node-end">流程已结束</div>
                    <div v-else-if="entry.startUser" class="flat-node-sub">{{ entry.startUser }}（发起流程）</div>
                    <div v-else-if="entry.assignText" class="flat-node-sub">
                      {{ entry.assignText }}<template v-if="entry.modeText">（{{ entry.modeText }}）</template><template v-else-if="entry.nodeTypeText">（{{ entry.nodeTypeText }}）</template>
                    </div>
                    <div v-else class="flat-node-sub node-explain">{{ entry.nodeExplain || '--' }}</div>
                    <div v-if="entry.assignList && entry.assignList.length" class="flat-assign-users">
                      <el-tag v-for="item in entry.assignList" :key="item.id" size="mini">{{ item.name }}</el-tag>
                    </div>
                  </div>
                  <div class="flat-right-meta">
                    <span class="flat-time">{{ entry.time }}</span>
                    <el-tag v-if="entry.nodeStatus === 'completed'" size="mini" type="success" class="flat-node-tag">已完成</el-tag>
                    <el-tag v-else-if="entry.nodeStatus === 'running'" size="mini" type="warning" class="flat-node-tag">进行中</el-tag>
                  </div>
                </div>

                <div v-if="entry.nodeType === 'TRIGGER' && entry.trigger" class="trigger-info">
                  <div class="trigger-status">
                    <el-tag size="mini" :type="getTriggerTagType(entry.trigger, !!entry.nodeEndTime)">
                      {{ getTriggerStatusText(entry.trigger, !!entry.nodeEndTime) }}
                    </el-tag>
                  </div>
                  <div v-if="entry.trigger.message" class="trigger-message">{{ entry.trigger.message }}</div>
                </div>
                <div v-if="entry.nodeType === 'SUBPROCESS' && entry.subprocess" class="subprocess-info">
                  <template v-if="entry.subprocess.processInstanceId">
                    <div class="subprocess-info-header">
                    <span class="subprocess-info-title">
                      <i class="el-icon-money"></i>{{ entry.subprocess.processDefinitionName }}
                    </span>
                    </div>
                    <div class="subprocess-info-body">
                      <div v-if="entry.subprocess.flowNo" class="subprocess-info-row subprocess-info-row-full">
                        <span class="subprocess-label">流水号</span>
                        <el-tag size="mini" type="primary" class="subprocess-value-tag">{{ entry.subprocess.flowNo }}</el-tag>
                      </div>
                      <div v-if="entry.subprocess.startUser" class="subprocess-info-row">
                        <span class="subprocess-label">发起人</span>
                        <span class="subprocess-value">{{ entry.subprocess.startUser }}</span>
                      </div>
                      <div v-if="entry.subprocess.startTime" class="subprocess-info-row subprocess-info-row-full">
                        <span class="subprocess-label">开始时间</span>
                        <span class="subprocess-value">{{ entry.subprocess.startTime }}</span>
                        <template v-if="entry.subprocess.endTime">
                          <span class="subprocess-time-sep">~</span>
                          <span class="subprocess-label">结束时间</span>
                          <span class="subprocess-value">{{ entry.subprocess.endTime }}</span>
                        </template>
                      </div>
                      <div v-if="entry.subprocess.bizStatus" class="subprocess-info-row subprocess-info-row-full">
                        <span class="subprocess-label">审批状态</span>
                        <el-tag size="mini" :type="getBizStatusTagType(entry.subprocess.bizStatus)" class="subprocess-biz-status">{{ entry.subprocess.bizStatus.name }}</el-tag>
                      </div>
                      <div v-if="entry.subprocess.deleteReason" class="subprocess-info-row subprocess-info-row-full">
                        <span class="subprocess-label">处理意见</span>
                        <span class="subprocess-value">{{ entry.subprocess.deleteReason }}</span>
                      </div>
                    </div>
                    <div class="subprocess-actions">
                      <el-button size="mini" type="primary" plain @click="openSubprocessDrawer(entry.subprocess)">
                        <i class="el-icon-view"></i>查看子流程详情
                      </el-button>
                    </div>
                  </template>
                  <template v-else>
                    <span class="subprocess-empty-tip">未查询到子流程信息</span>
                  </template>
                </div>
              </template>

              <!-- 任务节点条目 -->
              <template v-else-if="entry.type === 'task'">
                <div class="flat-task-header">
                  <div class="flat-task-user">
                    <span v-if="entry.nodeTypeText" class="flat-role-label">{{ entry.nodeTypeText }}</span>
                    <span class="flat-task-name">{{ entry.taskOwner }}</span>
                  </div>
                  <div class="flat-right-meta">
                    <span class="flat-time">{{ entry.time }}</span>
                    <template v-if="entry.taskEnded">
                      <el-tag v-if="entry.taskDeleted" size="mini" type="info">已取消</el-tag>
                      <el-tag v-else size="mini" type="success">已处理</el-tag>
                    </template>
                    <el-tag v-else size="mini" type="warning">待处理</el-tag>
                  </div>
                </div>
              </template>

              <!-- 操作记录条目 -->
              <template v-else>
                <div class="flat-record-header">
                  <div class="flat-user-block">
                    <span v-if="entry.nodeTypeText" class="flat-role-label">{{ entry.nodeTypeText }}</span>
                    <span class="flat-record-user">{{ entry.userName }}<el-tooltip v-if="entry.showTaskOwner" :content="'任务所属人：' + entry.showTaskOwner" placement="top"><span class="flat-role-owner">[ <i class="el-icon-user-solid"></i>{{ entry.showTaskOwner }} ]</span></el-tooltip></span>
                  </div>
                  <span class="flat-time">{{ entry.time }}</span>
                </div>
                <div class="flat-record-card">
                  <div class="item-left">
                    <span class="handle-type-tag" :class="getHandleTypeClass(entry.handleType)">{{ getHandleTypeText(entry.handleType) }}</span>
                  </div>
                  <div class="item-right">
                    <div v-if="entry.targetUser" class="item-header">
                      <div class="item-user">
                        <template v-if="entry.assignUser">
                          <i class="el-icon-s-custom item-operate"></i>
                          <span class="assign-user">{{ entry.assignUser }}</span>
                        </template>
                        <i class="el-icon-right item-arrow"></i>
                        <span class="target-user theme-text">{{ entry.targetUser }}</span>
                      </div>
                    </div>
                    <div v-if="hasItemContent(entry)" class="item-content">
                      <div v-if="entry.approval && entry.approval.approval && entry.approval.approval.remark" class="item-message">
                        {{ entry.approval.approval.remark }}
                      </div>
                      <div v-if="entry.opinion && entry.opinion.message" class="item-message">{{ entry.opinion.message }}</div>
                      <div v-if="entry.comment && entry.comment.message" class="item-message">{{ entry.comment.message }}</div>
                      <div v-if="entry.sign && entry.sign.message" class="item-sign">
                        <el-button type="text" size="mini" @click="showSign(entry.sign.message)">
                          <i class="el-icon-edit"></i>查看签名
                        </el-button>
                      </div>
                      <template v-if="entry.attachments">
                        <div v-if="entry.attachments.images && entry.attachments.images.length" class="attachment-box">
                          <a v-for="image in entry.attachments.images" :key="image.id" :href="image.url" target="_blank" class="attachment-item">
                            <i class="el-icon-picture-outline"></i>{{ image.name }}
                          </a>
                        </div>
                        <div v-if="entry.attachments.files && entry.attachments.files.length" class="attachment-box">
                          <a v-for="file in entry.attachments.files" :key="file.id" :href="file.url" target="_blank" class="attachment-item">
                            <i class="el-icon-document"></i>{{ file.name }}
                          </a>
                        </div>
                      </template>
                    </div>
                    <div v-else class="item-content empty-content">
                      <i class="el-icon-warning-outline"></i>
                      <span>没有可展示内容</span>
                    </div>
                  </div>
                </div>
              </template>

            </div>
          </div>
        </template>
        <template v-else>
          <div class="empty-hint">暂无操作记录</div>
        </template>
      </div>
    </template>

    <!-- 默认模式 -->
    <template v-else>
      <div class="history-timeline">
        <div v-for="(node, index) in historyNodes" :key="node.id" class="timeline-item">
          <!-- 左侧图标 -->
          <div class="timeline-left">
            <div class="node-avatar-wrapper">
              <div class="node-avatar" :class="getNodeAvatarClass(node)">
                <i :class="getNodeIcon(node)"></i>
              </div>
              <div v-if="getNodeStatus(node)" class="node-status" :class="getNodeStatusClass(node)">
                <i :class="getNodeStatusIcon(node)"></i>
              </div>
            </div>
            <div v-if="hasRecords(node)" class="expand-btn" @click="toggleExpand(node.id)">
              <i :class="isExpanded(node.id) ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
            </div>
            <div v-if="index < historyNodes.length - 1" class="timeline-line" :class="{ 'line-with-btn': hasRecords(node) }"></div>
          </div>

          <!-- 右侧内容 -->
          <div class="timeline-right">
            <div class="node-header">
              <div class="node-title">
                <div v-if="node.name" class="node-type">{{ node.name }}</div>
                <div v-if="node.id === 'root' && historyInfo.startUser" class="node-users">
                  {{ historyInfo.startUser.name }}（发起流程）
                </div>
                <div v-else-if="isEndEventNode(node)" class="node-users node-end">流程已结束</div>
                <div v-else-if="hasAssign(node)" class="node-users">
                  {{ getAssignText(node) }}<template v-if="shouldShowMode(node) && getModeText(node)">（{{ getModeText(node) }}）</template><template v-else-if="node.type">（{{ getNodeTypeText(node.type) }}）</template>
                </div>
                <div v-else class="node-users node-explain">{{ node.explain || '--' }}</div>
              </div>
              <div class="node-header-right">
                <div class="node-time">{{ node.endTime || node.createTime }}</div>
                <el-tag v-if="getNodeStatus(node) === 'completed'" size="mini" type="success">已完成</el-tag>
                <el-tag v-else-if="getNodeStatus(node) === 'running'" size="mini" type="warning">进行中</el-tag>
              </div>
            </div>

            <template v-if="getAssignList(node).length">
              <div class="assign-users">
                <el-tag v-for="item in getAssignList(node)" :key="item.id" size="mini">{{ item.name }}</el-tag>
              </div>
            </template>

            <div v-if="node.type === 'TRIGGER' && node.trigger" class="trigger-info">
              <div class="trigger-status">
                <el-tag size="mini" :type="getTriggerTagType(node.trigger, !!node.endTime)">
                  {{ getTriggerStatusText(node.trigger, !!node.endTime) }}
                </el-tag>
              </div>
              <div v-if="node.trigger.message" class="trigger-message">{{ node.trigger.message }}</div>
            </div>
            <div v-if="node.type === 'SUBPROCESS' && node.subprocess" class="subprocess-info">
              <template v-if="node.subprocess.processInstanceId">
                <div class="subprocess-info-header">
                <span class="subprocess-info-title">
                  <i class="el-icon-money"></i>{{ node.subprocess.processDefinitionName }}
                </span>
                </div>
                <div class="subprocess-info-body">
                  <div v-if="node.subprocess.flowNo" class="subprocess-info-row subprocess-info-row-full">
                    <span class="subprocess-label">流水号</span>
                    <el-tag size="mini" type="primary" class="subprocess-value-tag">{{ node.subprocess.flowNo }}</el-tag>
                  </div>
                  <div v-if="node.subprocess.startUser" class="subprocess-info-row">
                    <span class="subprocess-label">发起人</span>
                    <span class="subprocess-value">{{ node.subprocess.startUser }}</span>
                  </div>
                  <div v-if="node.subprocess.startTime" class="subprocess-info-row subprocess-info-row-full">
                    <span class="subprocess-label">开始时间</span>
                    <span class="subprocess-value">{{ node.subprocess.startTime }}</span>
                    <template v-if="node.subprocess.endTime">
                      <span class="subprocess-time-sep">~</span>
                      <span class="subprocess-label">结束时间</span>
                      <span class="subprocess-value">{{ node.subprocess.endTime }}</span>
                    </template>
                  </div>
                  <div v-if="node.subprocess.bizStatus" class="subprocess-info-row subprocess-info-row-full">
                    <span class="subprocess-label">审批状态</span>
                    <el-tag size="mini" :type="getBizStatusTagType(node.subprocess.bizStatus)" class="subprocess-biz-status">{{ node.subprocess.bizStatus.name }}</el-tag>
                  </div>
                  <div v-if="node.subprocess.deleteReason" class="subprocess-info-row subprocess-info-row-full">
                    <span class="subprocess-label">处理意见</span>
                    <span class="subprocess-value">{{ node.subprocess.deleteReason }}</span>
                  </div>
                </div>
                <div class="subprocess-actions">
                  <el-button size="mini" type="primary" plain @click="openSubprocessDrawer(node.subprocess)">
                    <i class="el-icon-view"></i>查看子流程详情
                  </el-button>
                </div>
              </template>
              <template v-else>
                <span class="subprocess-empty-tip">未查询到子流程信息</span>
              </template>
            </div>

            <div v-if="node.taskList && node.taskList.length && isExpanded(node.id)" class="task-group-list">
              <div v-for="(task, taskIndex) in node.taskList" :key="taskIndex" class="task-group">
                <div v-if="task.id" class="task-assignee">
                  <div class="user-avatar-small"><i class="el-icon-user"></i></div>
                  <span class="user-name-small">{{ task.owner && task.owner.name || '未知用户' }}</span>
                  <template v-if="node.id !== 'root'">
                    <template v-if="task.ended">
                      <el-tag v-if="task.deleted" size="mini" type="info" class="task-status-tag">已取消</el-tag>
                      <el-tag v-else size="mini" type="success" class="task-status-tag">已处理</el-tag>
                    </template>
                    <template v-else>
                      <el-tag size="mini" type="warning" class="task-status-tag">待处理</el-tag>
                    </template>
                  </template>
                </div>
                <template v-if="task.records && task.records.length">
                  <div v-for="(record, recordIndex) in task.records" :key="recordIndex" class="task-record" :class="{ 'task-record-indented': !!task.id }">
                    <div class="record-user">
                      <i class="el-icon-user-solid record-user-icon"></i>
                      <span class="record-user-name">{{ record.userInfo && record.userInfo.name || '未知用户' }}</span>
                    </div>
                    <template v-if="record.items && record.items.length">
                      <div class="record-items-tree">
                        <div v-for="(item, itemIndex) in record.items" :key="itemIndex" class="record-item-branch">
                          <div class="branch-line">
                            <div class="branch-vertical" :class="{ 'branch-last': itemIndex === record.items.length - 1 }"></div>
                            <div class="branch-horizontal"></div>
                          </div>
                          <div class="record-item">
                            <div class="item-left">
                              <span v-if="item.approval && item.approval.approval && item.approval.approval.handleType"
                                    class="handle-type-tag" :class="getHandleTypeClass(item.approval.approval.handleType)">
                                {{ getHandleTypeText(item.approval.approval.handleType) }}
                              </span>
                              <span v-else-if="item.opinion" class="handle-type-tag handle-type-info">意见</span>
                              <span v-else-if="item.comment" class="handle-type-tag handle-type-info">评论</span>
                              <span v-else class="handle-type-tag handle-type-info">操作</span>
                            </div>
                            <div class="item-right">
                              <div class="item-header">
                                <div v-if="item.approval && item.approval.approval && item.approval.approval.targetUser"
                                     class="item-user">
                                  <template v-if="item.approval.approval.assignUser">
                                    <i class="el-icon-s-custom item-operate"></i>
                                    <span class="assign-user">{{ item.approval.approval.assignUser.name }}</span>
                                  </template>
                                  <i class="el-icon-right item-arrow"></i>
                                  <span class="target-user theme-text">{{ item.approval.approval.targetUser.name }}</span>
                                </div>
                                <div v-else
                                     class="item-user">
                                  {{ record.userInfo && record.userInfo.name || '未知用户' }}
                                </div>
                                <span class="item-time">{{ getItemTime(item) }}</span>
                              </div>
                              <div v-if="hasItemContent(item)" class="item-content">
                                <div v-if="item.approval && item.approval.approval && item.approval.approval.remark" class="item-message">
                                  {{ item.approval.approval.remark }}
                                </div>
                                <div v-if="item.opinion && item.opinion.message" class="item-message">{{ item.opinion.message }}</div>
                                <div v-if="item.comment && item.comment.message" class="item-message">{{ item.comment.message }}</div>
                                <div v-if="item.sign && item.sign.message" class="item-sign">
                                  <el-button type="text" size="mini" @click="showSign(item.sign.message)">
                                    <i class="el-icon-edit"></i>查看签名
                                  </el-button>
                                </div>
                                <template v-if="item.attachments">
                                  <div v-if="item.attachments.images && item.attachments.images.length" class="attachment-box">
                                    <a v-for="image in item.attachments.images" :key="image.id" :href="image.url" target="_blank" class="attachment-item">
                                      <i class="el-icon-picture-outline"></i>{{ image.name }}
                                    </a>
                                  </div>
                                  <div v-if="item.attachments.files && item.attachments.files.length" class="attachment-box">
                                    <a v-for="file in item.attachments.files" :key="file.id" :href="file.url" target="_blank" class="attachment-item">
                                      <i class="el-icon-document"></i>{{ file.name }}
                                    </a>
                                  </div>
                                </template>
                              </div>
                              <div v-else class="item-content empty-content">
                                <i class="el-icon-warning-outline"></i>
                                <span>没有可展示内容</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- 签名弹窗 -->
    <DataGridDialog title="签名" :visible.sync="signDialogVisible" width="400px"
                    custom-class="flow-common-history-sign-dialog">
      <div class="sign-preview">
        <img v-if="currentSign" :src="currentSign" alt="签名" />
      </div>
    </DataGridDialog>

    <!-- 子流程详情抽屉 -->
    <DataGridDrawer :visible.sync="subprocessDrawer.visible" class="flow-subprocess-drawer">
      <div class="subprocess-drawer-body" v-loading="subprocessDrawer.loading" element-loading-text="加载中...">
        <div class="subprocess-drawer-header">
          <span class="subprocess-drawer-title">{{ subprocessDrawer.title }}</span>
          <el-radio-group v-if="subprocessDrawer.historyInfo && !timeline" v-model="subprocessDrawer.useTimeline" size="mini">
            <el-radio-button :label="false"><i class="el-icon-share"></i> 结构视图</el-radio-button>
            <el-radio-button :label="true"><i class="el-icon-time"></i> 时间线视图</el-radio-button>
          </el-radio-group>
        </div>
        <template v-if="subprocessDrawer.historyInfo">
          <div class="subprocess-drawer-history">
            <FlowCommonHistory :history-info="subprocessDrawer.historyInfo"
                               :timeline="subprocessDrawer.useTimeline"></FlowCommonHistory>
          </div>
        </template>
        <template v-else-if="!subprocessDrawer.loading">
          <div class="subprocess-drawer-error">
            <i class="el-icon-warning-outline"></i>
            <span>流转记录加载失败</span>
            <el-button size="mini" type="text" @click="loadSubprocessHistory(subprocessDrawer.processInstanceId)">重新加载</el-button>
          </div>
        </template>
      </div>
    </DataGridDrawer>
  </div>
</template>

<script>
import DataGridDialog from '../../../../../elements/grid/DataGridDialog'
import DataGridDrawer from '../../../../../elements/grid/DataGridDrawer'
export default {
  name: 'FlowCommonHistory',
  components: {
    DataGridDialog,
    DataGridDrawer
  },
  props: {
    historyInfo: {
      type: Object,
      required: true
    },
    timeline: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      expandedNodes: {},
      signDialogVisible: false,
      currentSign: '',
      subprocessDrawer: {
        visible: false,
        loading: false,
        title: '',
        processInstanceId: '',
        historyInfo: null,
        useTimeline: false
      }
    }
  },
  computed: {
    historyNodes () {
      if (!this.historyInfo || !this.historyInfo.nodeMap) return []
      const nodeMap = this.historyInfo.nodeMap
      const endList = this.historyInfo.endList || []
      const runningList = this.historyInfo.runningList || []
      const completedNodes = []
      for (let i = 0; i < endList.length; i++) {
        const node = nodeMap[endList[i]]
        if (node && node.id !== 'startEventNode') completedNodes.push(node)
      }
      const runningNodes = []
      for (let i = 0; i < runningList.length; i++) {
        const node = nodeMap[runningList[i]]
        if (node && node.id !== 'startEventNode') runningNodes.push(node)
      }
      return completedNodes.concat(runningNodes)
    },
    flatTimelineItems () {
      const result = []
      const nodes = this.historyNodes
      for (let ni = 0; ni < nodes.length; ni++) {
        const node = nodes[ni]
        const nodeTime = node.createTime || node.endTime || ''
        result.push({
          type: 'node',
          nodeId: node.id,
          nodeName: node.name || '',
          nodeType: node.type || '',
          nodeStartTime: node.startTime || '',
          nodeEndTime: node.endTime || '',
          nodeExplain: node.explain || '',
          nodeStatus: this.getNodeStatus(node),
          assignText: this.hasAssign(node) ? this.getAssignText(node) : '',
          modeText: this.shouldShowMode(node) ? this.getModeText(node) : '',
          nodeTypeText: node.type ? this.getNodeTypeText(node.type) : '',
          isEndEvent: this.isEndEventNode(node),
          startUser: node.id === 'root' && this.historyInfo.startUser ? this.historyInfo.startUser.name : '',
          assignList: this.getAssignList(node),
          subprocess: node.subprocess || null,
          trigger: node.trigger || null,
          time: nodeTime,
          _ts: nodeTime ? new Date(nodeTime).getTime() : 0
        })
        const taskList = node.taskList || []
        for (let ti = 0; ti < taskList.length; ti++) {
          const task = taskList[ti]
          if (task.id && node.id !== 'root') {
            const taskTime = task.createTime || task.endTime || ''
            result.push({
              type: 'task',
              nodeName: node.name || '',
              nodeTypeText: node.id === 'root' ? '发起人' : (node.type ? this.getNodeTypeText(node.type) + '人' : ''),
              taskOwner: (task.owner && task.owner.name) || '未知用户',
              taskEnded: task.ended,
              taskDeleted: task.deleted,
              time: taskTime,
              _ts: taskTime ? new Date(taskTime).getTime() : 0
            })
          }
          const ownerId = (task.owner && task.owner.id) || ''
          const records = task.records || []
          for (let ri = 0; ri < records.length; ri++) {
            const record = records[ri]
            const items = record.items || []
            const handlerId = (record.userInfo && record.userInfo.id) || ''
            for (let ii = 0; ii < items.length; ii++) {
              const item = items[ii]
              const approval = item.approval && item.approval.approval
              const handleType = approval && approval.handleType
                ? approval.handleType
                : (item.opinion ? 'OPINION' : (item.comment ? 'COMMENT' : null))
              const time = this.getItemTime(item)
              result.push({
                type: 'record',
                nodeName: node.name || '',
                nodeTypeText: node.id === 'root' ? '发起人' : (node.type ? this.getNodeTypeText(node.type) + '人' : ''),
                userName: (record.userInfo && record.userInfo.name) || '未知用户',
                showTaskOwner: ownerId && handlerId && ownerId !== handlerId ? (task.owner && task.owner.name) || '' : '',
                handleType: handleType,
                assignUser: approval && approval.assignUser && approval.assignUser.name,
                targetUser: approval && approval.targetUser && approval.targetUser.name,
                approval: item.approval,
                opinion: item.opinion,
                comment: item.comment,
                sign: item.sign,
                attachments: item.attachments,
                time: time,
                _ts: time ? new Date(time).getTime() : 0
              })
            }
          }
        }
      }
      // 节点条目按原始顺序固定，记录条目按时间排序插入节点之间
      // 策略：先按 _ts 排序，节点条目 _ts 相同时保持原顺序（stable sort）
      result.sort((a, b) => {
        if (a._ts !== b._ts) return a._ts - b._ts
      })
      return result
    }
  },
  methods: {
    showSign (signData) {
      this.currentSign = signData
      this.signDialogVisible = true
    },
    openSubprocessDrawer (subprocess) {
      this.subprocessDrawer.title = '流程记录 - ' + (subprocess.processDefinitionName || '子流程详情')
      this.subprocessDrawer.processInstanceId = subprocess.processInstanceId
      this.subprocessDrawer.historyInfo = null
      this.subprocessDrawer.useTimeline = this.timeline
      this.subprocessDrawer.visible = true
      this.loadSubprocessHistory(subprocess.processInstanceId)
    },
    loadSubprocessHistory (processInstanceId) {
      this.subprocessDrawer.loading = true
      this.subprocessDrawer.historyInfo = null
      this.$ajax({
        url: '/workspace/manager/flow/db/record/process/history',
        method: 'post',
        data: this.$qs.stringify({ id: processInstanceId })
      }).then(response => {
        if (response.data && response.data['responseCode'] === 'SUCCESS') {
          this.subprocessDrawer.historyInfo = response.data.historyInfo || {}
        } else {
          this.$message.warning((response.data && response.data['message']) || '加载失败')
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
      }).finally(() => {
        this.subprocessDrawer.loading = false
      })
    },
    hasRecords (node) {
      return !!(node.taskList && node.taskList.length)
    },
    isExpanded (nodeId) {
      if (this.expandedNodes[nodeId] === undefined) return true
      return this.expandedNodes[nodeId]
    },
    toggleExpand (nodeId) {
      this.$set(this.expandedNodes, nodeId, !this.isExpanded(nodeId))
    },
    isEndEventNode (node) {
      return node.id === 'endEventNode'
    },
    getNodeAvatarClass (node) {
      if (this.isEndEventNode(node)) return 'avatar-completed'
      const historyInfo = this.historyInfo
      if (historyInfo.runningList && historyInfo.runningList.indexOf(node.id) !== -1) return 'avatar-running'
      if (historyInfo.endList && historyInfo.endList.indexOf(node.id) !== -1) return 'avatar-completed'
      return 'avatar-pending'
    },
    getNodeIcon (node) {
      if (node.id === 'root') return 'el-icon-s-custom'
      if (this.isEndEventNode(node)) return 'el-icon-circle-check'
      if (node.type === 'APPROVAL') return 'el-icon-s-check'
      if (node.type === 'TASK') return 'el-icon-s-claim'
      if (node.type === 'CC') return 'el-icon-message'
      if (node.type === 'TRIGGER') return 'el-icon-set-up'
      if (node.type === 'DELAY') return 'el-icon-time'
      if (node.type === 'SUBPROCESS') return 'el-icon-money'
      const nodeName = node.name || ''
      if (nodeName.indexOf('结束') !== -1) return 'el-icon-circle-check'
      return 'el-icon-user'
    },
    getNodeStatus (node) {
      if (this.isEndEventNode(node)) return 'completed'
      const historyInfo = this.historyInfo
      if (historyInfo.endList && historyInfo.endList.indexOf(node.id) !== -1) return 'completed'
      if (historyInfo.runningList && historyInfo.runningList.indexOf(node.id) !== -1) return 'running'
      return ''
    },
    getNodeStatusClass (node) {
      const status = this.getNodeStatus(node)
      if (status === 'completed') return 'status-completed'
      if (status === 'running') return 'status-running'
      return ''
    },
    getNodeStatusIcon (node) {
      const status = this.getNodeStatus(node)
      if (status === 'completed') return 'el-icon-check'
      if (status === 'running') return 'el-icon-more'
      return ''
    },
    getNodeTypeText (type) {
      if (type === 'APPROVAL') return '审批'
      if (type === 'TASK') return '办理'
      if (type === 'CC') return '抄送'
      return '未知'
    },
    getModeText (node) {
      if (!node.mode) return ''
      if (node.mode === 'AND') return '会签'
      if (node.mode === 'OR') return '或签'
      if (node.mode === 'NEXT') return '顺序会签'
      return ''
    },
    shouldShowMode (node) {
      const userCount = node.assignUsers && node.assignUsers.length ? node.assignUsers.length : 0
      const hasRoles = node.assignRoles && node.assignRoles.length
      if (userCount <= 1 && !hasRoles) return false
      return true
    },
    hasAssign (node) {
      const hasUsers = node.assignUsers && node.assignUsers.length
      const hasRoles = node.assignRoles && node.assignRoles.length
      return hasUsers || hasRoles
    },
    getAssignText (node) {
      const users = node.assignUsers && node.assignUsers.length ? node.assignUsers : []
      const roles = node.assignRoles && node.assignRoles.length ? node.assignRoles : []
      const parts = []
      if (users.length > 0) parts.push(users.length + ' 人')
      if (roles.length > 0) parts.push(roles.length + ' 角色')
      return parts.join(' ')
    },
    getAssignList (node) {
      const users = node.assignUsers && node.assignUsers.length ? node.assignUsers : []
      const roles = node.assignRoles && node.assignRoles.length ? node.assignRoles : []
      return users.concat(roles)
    },
    getFlatNodeAvatarClass (entry) {
      if (entry.nodeStatus === 'running') return 'flat-avatar-running'
      if (entry.nodeStatus === 'completed') return 'flat-avatar-completed'
      return 'flat-avatar-pending'
    },
    getFlatNodeIcon (entry) {
      if (entry.isEndEvent) return 'el-icon-circle-check'
      if (entry.nodeId === 'root') return 'el-icon-s-custom'
      if (entry.nodeType === 'APPROVAL') return 'el-icon-s-check'
      if (entry.nodeType === 'TASK') return 'el-icon-s-claim'
      if (entry.nodeType === 'CC') return 'el-icon-message'
      if (entry.nodeType === 'TRIGGER') return 'el-icon-set-up'
      if (entry.nodeType === 'DELAY') return 'el-icon-time'
      if (entry.nodeType === 'SUBPROCESS') return 'el-icon-money'
      const name = entry.nodeName || ''
      if (name.indexOf('结束') !== -1) return 'el-icon-circle-check'
      return 'el-icon-user'
    },
    getFlatDotClass (entry) {
      if (entry.type === 'task') {
        if (entry.taskEnded && !entry.taskDeleted) return 'small-avatar-success'
        if (entry.taskDeleted) return 'small-avatar-info'
        return 'small-avatar-warning'
      }
      const ht = entry.handleType
      if (ht === 'AGREE' || ht === 'TRANSACT' || ht === 'RESOLVE') return 'small-avatar-success'
      if (ht === 'REFUSE' || ht === 'ROLLBACK' || ht === 'REVOKE') return 'small-avatar-danger'
      if (ht === 'TRANSFER' || ht === 'DELEGATE' || ht === 'ADD_MULTI') return 'small-avatar-warning'
      return 'small-avatar-info'
    },
    getHandleTypeText (handleType) {
      const handleTypeMap = {
        'AGREE': '同意',
        'REFUSE': '拒绝',
        'TRANSACT': '办理',
        'TRANSFER': '转办',
        'DELEGATE': '委派',
        'RESOLVE': '提交',
        'ROLLBACK': '回退',
        'REVOKE': '撤销',
        'ADD_MULTI': '加签',
        'COMMENT': '评论',
        'OPINION': '意见'
      }
      return handleTypeMap[handleType] || handleType || '操作'
    },
    getHandleTypeClass (handleType) {
      if (handleType === 'AGREE' || handleType === 'TRANSACT' || handleType === 'RESOLVE') return 'handle-type-success'
      if (handleType === 'REFUSE' || handleType === 'ROLLBACK' || handleType === 'REVOKE') return 'handle-type-danger'
      if (handleType === 'TRANSFER' || handleType === 'DELEGATE' || handleType === 'ADD_MULTI') return 'handle-type-warning'
      return 'handle-type-info'
    },
    getTriggerStatusText (trigger, isEnd) {
      const code = trigger.status && trigger.status.code
      if (code === 'ok') return '执行成功'
      if (code === 'fail') return '执行失败'
      if (!isEnd) return '执行中'
      return '无结果'
    },
    getTriggerTagType (trigger, isEnd) {
      const code = trigger.status && trigger.status.code
      if (code === 'ok') return 'success'
      if (code === 'fail') return 'danger'
      if (!isEnd) return 'warning'
      return 'info'
    },
    getBizStatusTagType (bizStatus) {
      if (!bizStatus) return 'info'
      switch (bizStatus.code) {
        case '1': return 'primary'
        case '2': return 'info'
        case '3': return 'danger'
        case '4': return 'success'
        default: return 'info'
      }
    },
    getItemTime (item) {
      if (item.approval && item.approval.createTime) return item.approval.createTime
      if (item.opinion && item.opinion.createTime) return item.opinion.createTime
      if (item.comment && item.comment.createTime) return item.comment.createTime
      return ''
    },
    hasItemContent (item) {
      if (item.approval && item.approval.approval && item.approval.approval.remark) return true
      if (item.opinion && item.opinion.message) return true
      if (item.comment && item.comment.message) return true
      if (item.sign && item.sign.message) return true
      if (item.attachments) {
        if (item.attachments.images && item.attachments.images.length) return true
        if (item.attachments.files && item.attachments.files.length) return true
      }
      return false
    }
  }
}
</script>

<style lang="scss">
.flow-common-history {
  .history-flat-timeline {
    .empty-hint {
      text-align: center;
      color: #909399;
      font-size: 13px;
      padding: 16px 0;
    }

    .flat-item {
      display: flex;
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;

        .flat-line {
          display: none;
        }
      }

      &.flat-item-node {
        .flat-left {
          width: 40px;
          margin-right: 12px;
        }
      }

      &.flat-item-task,
      &.flat-item-record {
        .flat-left {
          width: 40px;
          margin-right: 12px;
        }
      }

      .flat-left {
        flex: none;
        width: 40px;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-right: 12px;

        .flat-avatar-wrapper {
          position: relative;
          flex-shrink: 0;

          .flat-node-avatar {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
            color: #fff;

            &.flat-avatar-completed,
            &.flat-avatar-running {
              background-color: #409eff;
            }

            &.flat-avatar-pending {
              background-color: #909399;
            }
          }

          .flat-node-status {
            position: absolute;
            right: -2px;
            bottom: -2px;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            color: #fff;
            border: 2px solid #fff;

            &.status-completed { background-color: #67c23a; }
            &.status-running   { background-color: #e6a23c; }
          }
        }

        .flat-small-avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          color: #fff;
          flex-shrink: 0;

          &.small-avatar-success { background-color: #67c23a; }
          &.small-avatar-danger  { background-color: #f56c6c; }
          &.small-avatar-warning { background-color: #e6a23c; }
          &.small-avatar-info    { background-color: #909399; }
        }

        .flat-line {
          flex: 1;
          width: 1px;
          background-color: #dcdfe6;
          margin-top: 4px;
          min-height: 20px;
        }
      }

      .flat-right {
        flex: 1;
        min-width: 0;

        .flat-node-header {
          display: flex;
          align-items: flex-start;
          gap: 6px;

          .flat-node-left {
            flex: 1;
            min-width: 0;
          }

          .flat-node-name {
            font-size: 13px;
            font-weight: 600;
            color: #303133;
          }

          .flat-right-meta {
            flex: none;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 4px;
            margin-left: 8px;

            .flat-time {
              font-size: 12px;
              color: #909399;
              white-space: nowrap;
            }

            .flat-node-tag {
              flex-shrink: 0;
            }
          }
        }

        .flat-node-sub {
          margin-top: 2px;
          font-size: 12px;
          color: #606266;

          &.node-explain {
            color: #909399;
          }

          &.node-end {
            margin-top: 8px;
            color: #67c23a;
          }
        }

        .flat-assign-users {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 6px;
        }

        .flat-task-header {
          display: flex;
          align-items: flex-start;
          gap: 6px;

          .flat-task-user {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 2px;

            .flat-task-name {
              margin-top: 2px;
              font-size: 12px;
              font-weight: 500;
              color: #303133;
            }
          }

          .flat-right-meta {
            flex: none;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 4px;
            margin-left: auto;

            .flat-time {
              font-size: 12px;
              color: #909399;
              white-space: nowrap;
            }
          }
        }

        .flat-role-label {
          font-size: 13px;
          font-weight: 600;
          color: #303133;
        }

        .flat-role-owner {
          font-size: 11px;
          font-weight: 400;
          color: #909399;
          margin-left: 10px;
          i {
            margin-right: 3px;
          }
        }

        .flat-record-header {
          display: flex;
          align-items: flex-start;
          gap: 6px;

          .flat-user-block {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 1px;
          }

          .flat-time {
            font-size: 12px;
            color: #909399;
            white-space: nowrap;
            flex: none;
          }

          .flat-record-user {
            margin-top: 2px;
            font-size: 12px;
            font-weight: 500;
            color: #303133;
          }
        }

        .flat-record-card {
          display: flex;
          align-items: stretch;
          border-radius: 4px;
          overflow: hidden;
          background-color: #f8f9fa;
          margin-top: 6px;

          .item-left {
            flex-shrink: 0;
            width: 32px;
            display: flex;

            .handle-type-tag {
              width: 100%;
              display: flex;
              align-items: center;
              justify-content: center;
              writing-mode: vertical-rl;
              text-orientation: mixed;
              font-size: 11px;
              letter-spacing: 2px;

              &.handle-type-success { background-color: #e1f3d8; color: #67c23a; }
              &.handle-type-danger  { background-color: #fde2e2; color: #f56c6c; }
              &.handle-type-warning { background-color: #faecd8; color: #e6a23c; }
              &.handle-type-info    { background-color: #e9e9eb; color: #909399; }
            }
          }

          .item-right {
            flex: 1;
            min-width: 0;
            padding: 10px;

            .item-header {
              display: flex;
              align-items: baseline;
              gap: 4px;

              .item-user {
                font-size: 12px;
                color: #303133;
                font-weight: 500;
              }

              .item-operate {
                font-size: 11px;
                color: #606266;
              }

              .assign-user {
                font-size: 12px;
                color: #606266;
              }

              .item-arrow {
                font-size: 11px;
                color: #c0c4cc;
              }

              .target-user {
                font-size: 12px;
              }

              & ~ .item-content {
                margin-top: 4px;
              }
            }

            .item-content {
              &.empty-content {
                color: #c0c4cc;
                font-size: 11px;
              }

              .item-message {
                font-size: 12px;
                color: #606266;
                line-height: 1.6;
                word-break: break-all;

                & + .item-message {
                  margin-top: 1px;
                }

                & ~ .item-sign {
                  margin-top: 1px;
                }

                & ~ .attachment-box {
                  margin-top: 2px;
                }
              }

              .item-sign {
                .el-button {
                  padding: 0;
                  font-size: 11px;

                  i {
                    margin-right: 2px;
                  }
                }
              }

              .attachment-box {
                .attachment-item {
                  display: inline-flex;
                  align-items: baseline;
                  font-size: 11px;
                  color: #409eff;
                  margin-right: 10px;
                  text-decoration: none;

                  i { margin-right: 2px; }

                  &:hover { text-decoration: underline; }
                }

                & + .attachment-box {
                  margin-top: 0;
                }
              }
            }
          }
        }
      }
    }
  }

  .history-timeline {
    .timeline-item {
      display: flex;
      margin-bottom: 20px;

      &:last-child {
        margin-bottom: 0;

        .timeline-line {
          display: none;
        }
      }

      .timeline-left {
        flex: none;
        width: 40px;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-right: 12px;

        .node-avatar-wrapper {
          position: relative;
          flex-shrink: 0;
        }

        .node-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          color: #fff;

          &.avatar-completed,
          &.avatar-running {
            background-color: #409eff;
          }

          &.avatar-pending {
            background-color: #909399;
          }
        }

        .node-status {
          position: absolute;
          right: -2px;
          bottom: -2px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          color: #fff;
          border: 2px solid #fff;

          &.status-completed {
            background-color: #67c23a;
          }

          &.status-running {
            background-color: #e6a23c;
          }
        }

        .expand-btn {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background-color: #f5f7fa;
          border: 1px solid #dcdfe6;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          margin-top: 6px;
          font-size: 12px;
          color: #909399;
          transition: all 0.2s;

          &:hover {
            background-color: #e4e7ed;
            color: #606266;
          }
        }

        .timeline-line {
          flex: 1;
          width: 1px;
          background-color: #dcdfe6;
          margin-top: 8px;
          min-height: 30px;

          &.line-with-btn {
            margin-top: 6px;
          }
        }
      }

      .timeline-right {
        flex: 1;
        min-width: 0;

        .node-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 8px;

          .node-title {
            flex: 1;
            min-width: 0;

            .node-type {
              font-size: 13px;
              font-weight: 600;
              color: #303133;
            }

            .node-users {
              font-size: 14px;
              color: #303133;
              font-weight: 500;
              line-height: 1.5;
              margin-top: 2px;

              &.node-explain {
                color: #909399;
                font-size: 12px;
              }

              &.node-end {
                margin-top: 6px;
                color: #67c23a;
              }
            }
          }

          .node-header-right {
            flex: none;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 4px;
            margin-left: 16px;
          }

          .node-time {
            font-size: 12px;
            color: #909399;
            white-space: nowrap;
          }
        }

        .assign-users {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .task-group-list {
          margin-top: 12px;
        }

        .task-group {
          & + .task-group {
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px solid #f0f2f5;
          }

          .task-assignee {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 4px;

            .user-avatar-small {
              width: 22px;
              height: 22px;
              border-radius: 50%;
              background-color: #409eff;
              display: flex;
              align-items: center;
              justify-content: center;
              color: #fff;
              font-size: 12px;
              flex-shrink: 0;
            }

            .user-name-small {
              font-size: 13px;
              color: #303133;
              font-weight: 500;
              flex: 1;
            }

            .task-status-tag {
              flex-shrink: 0;
            }
          }

          .task-record {
            margin-bottom: 8px;

            &.task-record-indented {
              margin-left: 28px;
            }

            &:last-child {
              margin-bottom: 0;
            }

            .record-user {
              display: flex;
              align-items: center;
              gap: 4px;
              margin-bottom: 4px;

              .record-user-icon {
                font-size: 13px;
                color: #909399;
              }

              .record-user-name {
                font-size: 12px;
                color: #303133;
                font-weight: 500;
              }
            }

            .record-items-tree {
              padding-left: 8px;

              .record-item-branch {
                display: flex;
                align-items: stretch;
                margin-bottom: 6px;

                &:last-child {
                  margin-bottom: 0;
                }

                .branch-line {
                  flex-shrink: 0;
                  width: 14px;
                  position: relative;
                  margin-right: 6px;

                  .branch-vertical {
                    position: absolute;
                    left: 0;
                    top: 0;
                    bottom: -6px;
                    width: 1px;
                    background-color: #dcdfe6;

                    &.branch-last {
                      height: 12px;
                      bottom: auto;
                    }
                  }

                  .branch-horizontal {
                    position: absolute;
                    left: 0;
                    top: 12px;
                    width: 14px;
                    height: 1px;
                    background-color: #dcdfe6;
                  }
                }

                .record-item {
                  flex: 1;
                  min-width: 0;
                  display: flex;
                  align-items: stretch;
                  border-radius: 4px;
                  overflow: hidden;
                  background-color: #f8f9fa;
                  margin-top: 2px;

                  .item-left {
                    flex-shrink: 0;
                    width: 32px;
                    display: flex;

                    .handle-type-tag {
                      width: 100%;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      writing-mode: vertical-rl;
                      text-orientation: mixed;
                      font-size: 11px;
                      letter-spacing: 2px;

                      &.handle-type-success {
                        background-color: #e1f3d8;
                        color: #67c23a;
                      }

                      &.handle-type-danger {
                        background-color: #fde2e2;
                        color: #f56c6c;
                      }

                      &.handle-type-warning {
                        background-color: #faecd8;
                        color: #e6a23c;
                      }

                      &.handle-type-info {
                        background-color: #e9e9eb;
                        color: #909399;
                      }
                    }
                  }

                  .item-right {
                    flex: 1;
                    min-width: 0;
                    padding: 10px;

                    .item-header {
                      display: flex;
                      align-items: baseline;
                      gap: 4px;

                      .item-user {
                        font-size: 12px;
                        color: #303133;
                        font-weight: 500;
                      }

                      .item-operate {
                        font-size: 11px;
                        color: #606266;
                      }

                      .assign-user {
                        font-size: 12px;
                        color: #606266;
                      }

                      .item-arrow {
                        font-size: 11px;
                        color: #c0c4cc;
                      }

                      .target-user {
                        font-size: 12px;
                      }

                      .item-time {
                        font-size: 11px;
                        color: #909399;
                        margin-left: auto;
                      }

                      & ~ .item-content {
                        margin-top: 4px;
                      }
                    }

                    .item-content {
                      &.empty-content {
                        color: #c0c4cc;
                        font-size: 11px;
                      }

                      .item-message {
                        font-size: 12px;
                        color: #606266;
                        line-height: 1.6;
                        word-break: break-all;

                        & + .item-message {
                          margin-top: 1px;
                        }

                        & ~ .item-sign {
                          margin-top: 1px;
                        }

                        & ~ .attachment-box {
                          margin-top: 2px;
                        }
                      }

                      .item-sign {
                        .el-button {
                          padding: 0;
                          font-size: 11px;

                          i {
                            margin-right: 2px;
                          }
                        }
                      }

                      .attachment-box {
                        .attachment-item {
                          display: inline-flex;
                          align-items: baseline;
                          font-size: 11px;
                          color: #409eff;
                          margin-right: 10px;
                          text-decoration: none;

                          i {
                            margin-right: 2px;
                          }

                          &:hover {
                            text-decoration: underline;
                          }
                        }

                        & + .attachment-box {
                          margin-top: 0;
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  .trigger-info {
    margin-top: 8px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex-wrap: wrap;

    .trigger-status {
      flex-shrink: 0;
    }

    .trigger-message {
      font-size: 12px;
      color: #606266;
      line-height: 22px;
      word-break: break-all;
    }
  }

  .subprocess-info {
    margin-top: 10px;
    border-radius: 4px;
    border: 1px solid #d9ecff;
    border-left: 3px solid #409eff;
    background-color: #f0f7ff;
    overflow: hidden;
    padding: 7px 10px;

    .subprocess-info-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 6px;

      .subprocess-info-title {
        font-size: 12px;
        font-weight: 600;
        color: #303133;

        i {
          margin-right: 5px;
          color: #409eff;
          font-size: 13px;
        }
      }

      .subprocess-biz-status {
        flex-shrink: 0;
      }
    }

    .subprocess-info-body {
      display: flex;
      flex-wrap: wrap;
      gap: 4px 0;

      .subprocess-info-row {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;

        &.subprocess-info-row-full {
          width: 100%;
        }

        .subprocess-label {
          color: #909399;
          flex-shrink: 0;
          white-space: nowrap;

          &::after {
            content: '：';
          }
        }

        .subprocess-value {
          color: #303133;
        }

        .subprocess-value-tag {
          font-family: monospace;
          letter-spacing: 0.5px;
        }

        .subprocess-time-sep {
          color: #c0c4cc;
          margin: 0 2px;
        }
      }
    }

    .subprocess-empty-tip {
      font-size: 12px;
      color: #909399;
      padding: 8px 0;
    }

    .subprocess-actions {
      margin-top: 10px;

      .el-button {
        padding: 3px 8px;
        font-size: 12px;

        i {
          margin-right: 3px;
        }
      }
    }
  }

}

.flow-common-history-sign-dialog {
  .sign-preview {
    text-align: center;
    padding: 20px;
    background-color: #f5f7fa;
    border-radius: 4px;

    img {
      max-width: 100%;
      max-height: 300px;
    }
  }
}

.flow-subprocess-drawer {
  .subprocess-drawer-body {
    width: 640px;
    max-width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 200px;

    .subprocess-drawer-header {
      flex: none;
      padding: 16px 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid #e4e7ed;

      .subprocess-drawer-title {
        font-size: 15px;
        font-weight: 600;
        color: #303133;
      }

      .el-radio-group {
        .el-radio-button:first-child .el-radio-button__inner {
          border-radius: 30px 0 0 30px;
        }

        .el-radio-button:last-child .el-radio-button__inner {
          border-radius: 0 30px 30px 0;
        }
      }
    }

    .subprocess-drawer-history {
      flex: auto;
      overflow-y: auto;
      padding: 16px 20px;
    }

    .subprocess-drawer-error {
      flex: auto;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      color: #909399;
      font-size: 13px;

      i {
        font-size: 32px;
        color: #c0c4cc;
      }
    }
  }
}
</style>
